import { currentFormat } from '@/utils/cases';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { IconButton, Paper, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { Iconic } from '@/commons/iconic';
import { translates } from '@/utils';
import KanbanTaskCard from '../KanbanTaskCard';
import { IKanbanCard, IKanbanColumn } from '../types';
import KanbanColumnToolBar from './KanbanColumnToolBar';

export type onClickTaskCardProps = (idProduct: string, idColumn: string) => void;

type Props = {
  orderId: string;
  column: IKanbanColumn;
  setBoard: any;
  cards: Record<string, IKanbanCard>;
  index: number;
  onClickTaskCard?: onClickTaskCardProps;
  onReload: onClickTaskCardProps;
};

export default function KanbanColumn({
  column,
  index,
  cards,
  setBoard,
  orderId,
  onClickTaskCard,
  onReload,
}: Props) {
  // const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const handleUpdateColumn = async (newName: string) => {
    try {
      if (newName !== column.name) {
        setBoard((prev: any) => ({
          ...prev,
          columns: {
            ...prev.columns,
            [column.id]: {
              ...prev.columns[column.id],
              name: newName,
            },
          },
        }));
        enqueueSnackbar('Update success!');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteColumn = async () => {
    try {
      if (orderId === column.id) {
        return;
      }
      if (column.cardIds.length) {
        enqueueSnackbar('Column has tasks, please delete tasks first!');
        return;
      }
      setBoard((prev: any) => {
        const newColumnOrder = prev.columnOrder.filter((id: string) => id !== column.id);
        const newColumns = Object.keys(prev.columns).reduce((acc: any, key: string) => {
          if (key !== column.id) {
            acc[key] = prev.columns[key];
          }
          return acc;
        }, {});
        return {
          ...prev,
          columnOrder: newColumnOrder,
          columns: newColumns,
        };
      });
      enqueueSnackbar('Delete success!');
    } catch (error) {
      console.error(error);
    }
  };
  const calculatePrice = useCallback(() => {
    const priceArray = column.cardIds
      .map((cardId) => cards[cardId])
      .reduce((prev, current) => prev + current.price, 0);
    return priceArray;
  }, [cards, column.cardIds]);
  return (
    <Draggable draggableId={column.id} index={index} key={column.id}>
      {(provided) => (
        <Paper
          {...provided.draggableProps}
          ref={provided.innerRef}
          variant="outlined"
          sx={{
            px: 2,
            pb: 3,
            borderRadius: 1,
            borderStyle: 'dashed',
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? 'grey.100' : 'background.default',
          }}
        >
          <Stack spacing={3} {...provided.dragHandleProps}>
            <KanbanColumnToolBar
              disabled={column.id === orderId}
              columnName={column.name}
              onDelete={handleDeleteColumn}
              onUpdate={handleUpdateColumn}
            />
            <Droppable droppableId={column.id} type="task">
              {(columnProvided) => (
                <Stack
                  ref={columnProvided.innerRef}
                  {...columnProvided.droppableProps}
                  spacing={2}
                  sx={{ width: 280 }}
                >
                  {column.cardIds.map((cardId, cardIndex) => (
                    <Stack sx={{ position: 'relative' }}>
                      {cards[cardId]?.parentId && (
                        <IconButton
                          sx={{ position: 'absolute', top: 0, right: 5, zIndex: 10 }}
                          onClick={() => onReload(cardId, column.id)}
                        >
                          <Iconic icon="pepicons-pop:reload" color="purple" />
                        </IconButton>
                      )}
                      <KanbanTaskCard
                        onClick={onClickTaskCard}
                        key={cardId + index}
                        columnId={column.id}
                        index={cardIndex}
                        onDeleteTask={() => null}
                        card={cards[cardId]}
                      />
                    </Stack>
                  ))}
                  <Typography
                    noWrap
                    variant="body1"
                    sx={{
                      pr: 1,
                      pl: 1,
                    }}
                  >
                    <strong>{translates.total}:</strong> {currentFormat(calculatePrice() ?? 0)}
                  </Typography>
                  {columnProvided.placeholder}
                </Stack>
              )}
            </Droppable>
          </Stack>
        </Paper>
      )}
    </Draggable>
  );
}
